import type { PropsWithChildren } from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import { logout } from '@/features/auth/authThunks';
import BrandLogo from '@/components/common/BrandLogo.tsx';
import LeftArrow from '../icons/LeftArrow';
import LangSwitcher from '@/components/common/LangSwitcher';
import type { AuthUser } from '@/features/auth/types';

type AuthLayoutProps = PropsWithChildren<{
  title: React.ReactNode;
  subtitle?: string;
  background?: 'solid' | 'gradient';
  returnIcon?: boolean;
  success?: boolean;
  isCreateWorkspace?: boolean;
  isNew?: boolean;
  user?: AuthUser;
}>;

export default function AuthLayout({
  title,
  subtitle,
  background = 'gradient',
  children,
  returnIcon = false,
  success = false,
  isCreateWorkspace = false,
  isNew = false,
  user,
}: AuthLayoutProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const bgClass = background === 'gradient' ? 'bg-gradient-page-auth' : 'bg-background';

  const handleReturnClick = useCallback(async () => {
    if (isNew) {
      const res = await dispatch(logout());
      if (logout.fulfilled.match(res)) {
        navigate('/login', { replace: true });
      }
    } else {
      window.history.back();
    }
  }, [isNew, dispatch, navigate]);

  return (
    <div className={`relative min-h-svh ${bgClass}`}>
      <div className="absolute right-4 top-4 md:right-10 md:top-6 z-20">
        <LangSwitcher />
      </div>

      <div
        className={`hidden lg:flex lg:pointer-events-none lg:fixed ${isCreateWorkspace ? 'lg:right-0' : 'lg:left-0'} lg:inset-y-12 lg:w-[48vw] lg:overflow-hidden lg:items-center lg:justify-start`}
      >
        <div className="absolute inset-0  opacity-20" />
        <img
          src={isCreateWorkspace ? '/assets/login-screen-black.png' : '/assets/login-screen.png'}
          alt="login-screen"
          className={`relative h-full w-full object-contain ${isCreateWorkspace ? 'object-right' : 'object-left'}`}
          loading="eager"
        />
      </div>

      <div className="relative min-h-svh">
        <div
          className={`
            mx-auto min-h-svh
            max-w-[1200px]
            grid grid-cols-1 ${isCreateWorkspace ? 'lg:grid-cols-[560px_1fr]' : 'lg:grid-cols-[1fr_560px]'}
            gap-8 px-6 md:px-10
          `}
        >
          {isCreateWorkspace ? (
            <>
              <div className="flex items-center justify-center py-12">
                <div className="w-full max-w-[460px] lg:mr-auto text-white">
                  <div className="mb-16">
                    <p className="text-3xl font-semibold text-highlighted-reverse ">
                      {user?.firstName},
                    </p>
                    <h1 className="text-3xl font-semibold  flex items-center">
                      {title}{' '}
                      <span className="ml-2">
                        <BrandLogo className="mb-0" />
                      </span>
                    </h1>
                    {subtitle ? <p className="mt-2 text-sm opacity-80">{subtitle}</p> : null}
                  </div>
                  {children}
                </div>
              </div>
              <div className="hidden lg:block" aria-hidden />
            </>
          ) : (
            <>
              <div className="hidden lg:block" aria-hidden />
              <div className="flex items-center justify-center py-12">
                <div className="w-full max-w-[460px] lg:ml-auto text-white">
                  <div className="mb-16">
                    <BrandLogo />
                    <h1 className="text-3xl font-semibold tracking-tight flex items-center">
                      {returnIcon && !success && (
                        <span onClick={handleReturnClick}>
                          <LeftArrow className="size-8 mr-2 cursor-pointer " />
                        </span>
                      )}
                      {title}
                    </h1>
                    {subtitle ? <p className="mt-2 text-sm opacity-80">{subtitle}</p> : null}
                  </div>
                  {children}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
